import { type ComponentProps, forwardRef, useCallback } from 'react';
import Animated from 'react-native-reanimated';

import type { ScrollspyScrollViewRef } from '../../useScrollspy';

// ─────────────────────────────────────────────────────────────────────────────

export const ScrollspyScrollView = forwardRef<Animated.ScrollView, ScrollspyScrollViewProps>(
  (props, ref) => {
    const { children, onContentSizeChange, ...scrollViewProps } = props;

    // ─── Handlers ──────────────────────────────────────────────────────────

    const handleContentSizeChange = useCallback(
      (width: number, height: number) => {
        if (typeof onContentSizeChange === 'function') {
          onContentSizeChange(width, height);
        }
      },
      [onContentSizeChange],
    );

    // ─────────────────────────────────────────────────────────────────────

    return (
      <Animated.ScrollView
        ref={ref as ScrollspyScrollViewRef}
        scrollEventThrottle={16}
        onContentSizeChange={handleContentSizeChange}
        {...scrollViewProps}
      >
        {children}
      </Animated.ScrollView>
    );
  },
);

ScrollspyScrollView.displayName = 'ScrollspyScrollView';

// ─── Types ───────────────────────────────────────────────────────────────────

type ScrollspyScrollViewProps = Omit<
  ComponentProps<typeof Animated.ScrollView>,
  'ref' | 'horizontal'
>;
